import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CheckoutClaimLayout } from "@/features/onboarding/CheckoutClaimLayout";
import { Button } from "@/design-system/primitives";
import { ApiError, apiFetch } from "@/lib/api/client";
import type { LabCheckoutStatus } from "@/types/labs";
import {
  clearLabCheckoutHandoff,
  readLabCheckoutHandoff,
} from "./labCheckoutStorage";

export function validLabClaimLink(value: string): boolean {
  try {
    const url = new URL(value);
    const authUrl = new URL(import.meta.env.VITE_SUPABASE_URL);
    return (
      url.protocol === "https:" &&
      url.origin === authUrl.origin &&
      url.pathname === "/auth/v1/verify"
    );
  } catch {
    return false;
  }
}

export function LabClaimScreen() {
  const [params] = useSearchParams();
  const checkoutId = params.get("checkout_id") || "";
  const token = readLabCheckoutHandoff(checkoutId);
  const [state, setState] = useState<"checking" | "login" | "error">(
    "checking",
  );
  const [message, setMessage] = useState(
    "Confirming your payment and preparing your test order.",
  );
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!checkoutId || !token) {
      setState("error");
      setMessage(
        "This checkout link is missing or has expired. Sign in to check your blood tests.",
      );
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();
    const started = Date.now();
    setState("checking");
    setMessage("Confirming your payment and preparing your test order.");
    const deadline = setTimeout(() => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
      setState("error");
      setMessage(
        "Your order is taking longer to confirm. Check again below; you do not need to purchase again.",
      );
    }, 60_000);
    const request = (path: string) =>
      apiFetch<LabCheckoutStatus & { action_link?: string }>(path, {
        method: "POST",
        headers: { Authorization: "" },
        body: JSON.stringify({
          checkout_id: checkoutId,
          checkout_token: token,
        }),
        signal: controller.signal,
      });
    async function check() {
      try {
        const status = await request("/labs/checkout/status");
        if (cancelled) return;
        if (status.status === "paid") {
          const claim = await request("/labs/checkout/claim");
          if (cancelled) return;
          if (!claim.action_link || !validLabClaimLink(claim.action_link))
            throw new Error("Invalid sign-in link");
          clearTimeout(deadline);
          clearLabCheckoutHandoff(checkoutId);
          window.location.assign(claim.action_link);
          return;
        }
        if (status.status === "cancelled" || status.status === "refunded") {
          clearTimeout(deadline);
          setState("error");
          setMessage(
            status.status === "refunded"
              ? "This payment has been refunded. Sign in for your latest order information."
              : "This payment was cancelled. Sign in to view your blood tests.",
          );
          return;
        }
        if (
          status.status === "pending_payment" &&
          !["processing", "succeeded"].includes(status.payment_status ?? "")
        ) {
          clearTimeout(deadline);
          setState("error");
          setMessage(
            "The payment has not completed. Return to the checkout page to finish the same payment.",
          );
          return;
        }
        timer = setTimeout(check, 2000);
      } catch (error) {
        if (cancelled) return;
        if (
          error instanceof ApiError &&
          error.status === 409 &&
          error.code === "account_pre_existed"
        ) {
          clearTimeout(deadline);
          clearLabCheckoutHandoff(checkoutId);
          setState("login");
          setMessage(
            "Your order is linked to an existing account. Sign in to view your blood test.",
          );
        } else if (
          (error instanceof TypeError ||
            (error instanceof ApiError &&
              (error.status >= 500 ||
                [404, 408, 429, 499].includes(error.status)))) &&
          Date.now() - started < 55_000
        ) {
          timer = setTimeout(check, 3000);
        } else {
          clearTimeout(deadline);
          setState("error");
          setMessage(
            "We could not finish sign-in for this checkout. Check again or sign in to your account; do not purchase again.",
          );
        }
      }
    }
    void check();
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
      clearTimeout(deadline);
    };
  }, [checkoutId, token, retry]);

  return (
    <CheckoutClaimLayout
      pending={state === "checking"}
      title={
        state === "checking"
          ? "Confirming your order…"
          : state === "login"
            ? "Welcome back."
            : "Let’s check your order."
      }
      message={message}
      messageRole={state === "error" ? "alert" : "status"}
    >
      {state !== "checking" && (
        <div className="flex flex-col gap-12 mt-25">
          {state === "error" && token && (
            <Button onClick={() => setRetry((value) => value + 1)}>
              Check again
            </Button>
          )}
          <Button asChild variant={state === "login" ? "primary" : "secondary"}>
            <Link to="/login" state={{ from: { pathname: "/app/orders" } }}>
              Sign in <ArrowRight size={15} />
            </Link>
          </Button>
        </div>
      )}
    </CheckoutClaimLayout>
  );
}
